<template>
  <section
    class="py-20 sm:py-28 relative overflow-hidden bg-gradient-to-b from-slate-50 to-white"
    data-aos="fade"
    data-aos-duration="800"
  >
    <!-- Refined Background Elements -->
    <div class="absolute inset-0 overflow-hidden">
      <!-- Main background gradients with smoother animations -->
      <div
        class="absolute -top-40 -right-40 w-96 h-96 bg-green-800/20 rounded-full blur-3xl transform-gpu animate-subtle-float"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-96 h-96 bg-green-800/20 rounded-full blur-3xl transform-gpu animate-subtle-float-reverse"
      ></div>
      <div
        class="absolute top-1/3 right-1/3 w-64 h-64 bg-[#164364]/20 rounded-full blur-3xl transform-gpu animate-subtle-pulse"
      ></div>

      <!-- Refined Grid Pattern -->
      <div
        class="absolute inset-0 opacity-[0.02]"
        style="
          background-image: radial-gradient(#2f855a 0.5px, transparent 0.5px);
          background-size: 30px 30px;
          background-position: center center;
        "
      ></div>
    </div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Improved Section Header -->
      <div
        class="text-center max-w-3xl mx-auto mb-20"
        data-aos="fade-up"
        data-aos-duration="800"
      >
        <div class="inline-block mb-4">
          <div class="flex items-center justify-center space-x-3">
            <div class="w-12 h-1 bg-green-800 rounded-full animate-width"></div>
            <p
              class="text-green-800 font-semibold uppercase tracking-wider text-base"
            >
              Smart Farming
            </p>
            <div class="w-12 h-1 bg-green-800 rounded-full animate-width"></div>
          </div>
        </div>

        <h2
          class="text-5xl md:text-6xl font-bold text-[#164364] mb-6 leading-tight"
        >
          Transforming Agriculture
          <span class="text-green-800">with Technology</span>
        </h2>

        <p
          class="text-gray-800 text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed"
        >
          Our platform bridges critical gaps in the agricultural ecosystem,
          empowering farmers with tools for sustainable growth.
        </p>
      </div>

      <!-- Refined Feature Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <!-- Feature 1 -->
        <div
          class="feature-card group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 flex flex-col h-full"
        >
          <div class="relative h-56 overflow-hidden">
            <img
              :src="require('@/assets/images/villagesnl1.jpeg')"
              alt="Village savings"
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-[#164364]/90 to-transparent group-hover:from-[#164364] transition-all duration-500"
            ></div>
            <h3
              class="absolute bottom-4 left-4 text-2xl font-bold text-white transform group-hover:translate-y-[-4px] transition-transform duration-300"
            >
              Village Savings & Loans
            </h3>
          </div>

          <div class="p-8 flex-grow flex flex-col">
            <p class="text-gray-800 text-xl mb-6 flex-grow">
              Access affordable financing through community savings groups
              designed for rural farmers.
            </p>
          </div>
        </div>

        <!-- Feature 2 -->
        <div
          class="feature-card group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 flex flex-col h-full"
        >
          <div class="relative h-56 overflow-hidden">
            <img
              :src="require('@/assets/images/market.jpeg')"
              alt="Market access"
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-all duration-500"
            ></div>
            <h3
              class="absolute bottom-4 left-4 text-2xl font-bold text-white transform group-hover:translate-y-[-4px] transition-transform duration-300"
            >
              Market Access
            </h3>
          </div>

          <div class="p-8 flex-grow flex flex-col">
            <p class="text-gray-700 text-xl mb-6 flex-grow">
              Connect directly with buyers, eliminating middlemen and securing
              better prices for your produce.
            </p>
          </div>
        </div>

        <!-- Feature 3 -->
        <div
          class="feature-card group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 flex flex-col h-full"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="400"
        >
          <div class="relative h-56 overflow-hidden">
            <img
              :src="require('@/assets/images/transport1.jpg')"
              alt="Transport logistics"
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-[#164364]/90 to-transparent group-hover:from-[#164364] transition-all duration-500"
            ></div>
            <h3
              class="absolute bottom-4 left-4 text-2xl font-bold text-white transform group-hover:translate-y-[-4px] transition-transform duration-300"
            >
              Seamless Transport
            </h3>
          </div>

          <div class="p-8 flex-grow flex flex-col">
            <p class="text-gray-800 text-xl mb-6 flex-grow">
              Move your produce to market with ease. Our logistics network
              connects farmers with reliable transporters for timely delivery.
            </p>
          </div>
        </div>
        <!-- Feature 4 -->
        <div
          class="feature-card group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 flex flex-col h-full"
        >
          <div class="relative h-56 overflow-hidden">
            <img
              :src="require('@/assets/images/weather1.jpg')"
              alt="Weather intelligence"
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-all duration-500"
            ></div>
            <h3
              class="absolute bottom-4 left-4 text-2xl font-bold text-white transform group-hover:translate-y-[-4px] transition-transform duration-300"
            >
              Weather Intelligence
            </h3>
          </div>

          <div class="p-8 flex-grow flex flex-col">
            <p class="text-gray-700 text-xl mb-6 flex-grow">
              Receive localized forecasts and advisories to protect your crops
              and plan farming activities.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ModernFeaturesSection",
  mounted() {
    // Setup lightweight staggered animations for feature cards on scroll
    this.setupAnimations();
  },
  methods: {
    setupAnimations() {
      // Simple intersection observer for fade-in animations
      if (window.IntersectionObserver) {
        const featureCards = document.querySelectorAll(".feature-card");

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry, index) => {
              if (entry.isIntersecting) {
                // Add a slight delay based on index for staggered effect
                setTimeout(() => {
                  entry.target.classList.add("animated");
                }, 150 + index * 100);

                // Stop observing once animated
                observer.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
        );

        featureCards.forEach((card) => {
          card.style.opacity = "0";
          card.style.transform = "translateY(30px)";
          observer.observe(card);
        });
      }
    },
  },
  beforeUnmount() {
    // Clean up any event listeners if needed
  },
};
</script>

<style scoped>
/* Base styles for animations */
.feature-card {
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
}

.feature-card.animated {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

/* Modern subtle animations */
@keyframes subtle-float {
  0%,
  100% {
    transform: translateY(0) translateX(0);
  }
  50% {
    transform: translateY(-10px) translateX(5px);
  }
}

@keyframes subtle-float-reverse {
  0%,
  100% {
    transform: translateY(0) translateX(0);
  }
  50% {
    transform: translateY(10px) translateX(-5px);
  }
}

@keyframes subtle-pulse {
  0%,
  100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.05);
  }
}

@keyframes width-animation {
  0%,
  100% {
    width: 48px;
  }
  50% {
    width: 64px;
  }
}

@keyframes gradient-text {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Apply animations */
.animate-subtle-float {
  animation: subtle-float 12s ease-in-out infinite;
}

.animate-subtle-float-reverse {
  animation: subtle-float-reverse 12s ease-in-out infinite;
}

.animate-subtle-pulse {
  animation: subtle-pulse 8s ease-in-out infinite;
}

.animate-width {
  animation: width-animation 8s ease-in-out infinite;
}

/* Disable animations for users who prefer reduced motion */
@media (prefers-reduced-motion: reduce) {
  .feature-card {
    transition: none;
    opacity: 1 !important;
    transform: none !important;
  }

  .animate-subtle-float,
  .animate-subtle-float-reverse,
  .animate-subtle-pulse,
  .animate-width,
  .animate-gradient-text {
    animation: none;
  }
}
</style>
